// CSS
import './Cache.css';
// React hooks
import { useState, useEffect } from 'react';
// Skybase hooks
import { useQuery } from '../../context/QueryContext';
import { useAuth } from '../../context/AuthContext';
// Skybase components
import Sidebar from '../../components/Sidebar/Sidebar';
import FullResultDoc from '../../components/ResultInstance/FullResult/FullResultDoc/FullResultDoc';
import FullScreenModal from '../../components/Modals/FullScreenModal/FullScreenModal';
import DocumentPreviewModal from '../../components/Modals/FullScreenModal/DocumentPreviewModal/DocumentPreviewModal';
import InformationalPopover from '../../components/Popovers/InformationalPopover/InformationalPopover';
// External hooks/components
import { useAuth as useClerkAuth } from '@clerk/react';
import { useMediaQuery } from 'react-responsive';
// Media assets
import CalendarIcon from '../../assets/StandardIcons/TimeIcons/calendar-icon.png';
import ClockIcon from '../../assets/StandardIcons/TimeIcons/clock-icon.png';
import { useNavigate } from 'react-router-dom';



// =====
// Cache – represents the /cache Product page.
// =====
function Cache(){

    // destructure GC
    const { selectedSearchResultJson } = useQuery();
    const { isSubscribed, checkIsSubscribed, activeUserJson } = useAuth();
    const { getToken, isLoading: isClerkLoading } = useClerkAuth();

    // init hooks
    const navigate = useNavigate('');

    // responsive CSS
    const isDesktop = useMediaQuery({ minWidth: 1025 });

    
    // ================
    // onMount Behavior
    // ================
    // onMount --> check for valid Skybase Stripe subscription
    const [isInformationalPopoverOpen, setIsInformationalPopoverOpen] = useState(false);
    useEffect(() => {
        
        // Async. fn that prevents CurrentUsers without an active subscription from interacting with this page.
        const checkSubscription = async () => {
            // Don't perform a subscription-check if the Clerk User hasn't loaded yet.
            if (isClerkLoading) return;

            // Does CurrentUser have an active subscription?
            const subscribed = await checkIsSubscribed();

            // If NO: Display InformationalPopover.
            if (!subscribed) { setIsInformationalPopoverOpen(true); }
        };

        // Run the async. subscription-check
        checkSubscription();
        
        // Edge case: "bfcache === true" --> Forcibly reload the page to re-run the async. subscription-check
        const handlePageShow = (event) => {
            if (event.persisted) { window.location.reload(); }
        };
        window.addEventListener("pageshow", handlePageShow); // "pageShow" event listener
        
        
        // Edge case: No <Clerk JWT> --> Immediately redirect user from this page.
        checkForValidClerkTokenOtherwiseRedirect();

        // Remove listeners onDismount (prevent memory overflow)
        return () => { window.removeEventListener("pageshow", handlePageShow); };
    }, []);

    // ==========================================
    // checkForValidClerkTokenOtherwiseRedirect()
    // ==========================================
    // Checks for a valid clerk token. Forcibly re-directs the user to '/login' if not.
    async function checkForValidClerkTokenOtherwiseRedirect() {
        let token = await getToken();
        if (!token) { navigate('../login') };
    }
    // onChange ('activeUserJson') --> immediately check again for valid subscription
    useEffect(() => {
        checkForValidClerkTokenOtherwiseRedirect();
    }, [activeUserJson]);


    // ======================
    // Document Preview Modal – track Document Preview Modal state
    // ======================
    const [targetDocumentUUID, setTargetDocumentUUID] = useState(null);                     // default: NULL
    const [isDocumentPreviewModalOpen, setIsDocumentPreviewModalOpen] = useState(false);    // default: FALSE (closed)
    // openDocument(id: String) – open the target Document in the DocumentPreviewModal
    const openDocument = (id) => {
        setTargetDocumentUUID(id);
        setIsDocumentPreviewModalOpen(true);
    }
    // closeDocument() – close the DocumentPreviewModal
    const closeDocument = () => {
        setIsDocumentPreviewModalOpen(false);
    }

    

    // JSX Component
    return(
        <div className='Layout'>
            
            {/* Sidebar */}
            <Sidebar/>

            {/* Main content */}
            <div className='Cache'>

                {/* Search query */}
                <div className='Cache-search-query'>
                    {isDesktop && <span className='Cache-search-query--label-text'>Previous results for: </span>}
                    <span className='Cache-search-query--query-text'>"{selectedSearchResultJson?.search_query}"</span>
                </div>

                {/* Metadata row */}
                <div className='Cache-metadata-row'>

                    {/* ..date */}
                    <div className='Cache-metadata-row-chip'><img src={CalendarIcon} className='Cache-chip-icon'/>{new Date(selectedSearchResultJson?.created_at).toLocaleDateString("en-US", {month: "long", day: "numeric", year: "numeric" })}</div>

                    {/* ..time */}
                    <div className='Cache-metadata-row-chip'><img src={ClockIcon} className='Cache-chip-icon'/>{new Date(selectedSearchResultJson?.created_at).toLocaleTimeString("en-US", {hour: "numeric", minute: "2-digit", hour12: true, timeZone: "America/New_York"})}</div>

                    {/* ..# results */}
                    <div className='Cache-metadata-row-chip'>{selectedSearchResultJson?.search_results.length} results</div>
                </div>

                {/* Cached results repeater content */}
                <div className='Cache-cached-results-repeater-content'>
                    
                    {/* ..repeater */}
                    <div className='Cache-cached-results-repeater'>
                        {selectedSearchResultJson?.search_results.map( (resultJson, index) => (
                            <FullResultDoc
                                key={index}
                                uuid={resultJson.id}
                                onOpenCallback={() => openDocument(resultJson.id)}
                                onCloseCallback={() => closeDocument()}
                            />
                        ))}
                    </div>

                </div>
            </div>

            {/* DocumentPreviewModal */}
            <FullScreenModal
                isOpen={(isDocumentPreviewModalOpen)}
                onClose={closeDocument}
                children={
                    <DocumentPreviewModal
                        uuid={targetDocumentUUID}
                        triggerClose={() => setIsDocumentPreviewModalOpen(false)}
                        isReadOnly={true}
                    />
                }
            />

            
            {/* InformationalPopover */}
            { isInformationalPopoverOpen &&
                <InformationalPopover
                    titleText={"Subscription Required"}
                    bodyText={"Please purchase a Skybase subscription plan to continue."}
                    primaryButtonText={"View Subscription Plans"}
                    primaryButtonCallback={() => navigate('../pricing')}
                    secondaryButtonText={"Return Home"}
                    secondaryButtonCallback={() => navigate('../')}
                />
            }
        </div>
    )
}


// Export statement
export default Cache;